import PropTypes from 'prop-types';
import { Dispatch, forwardRef, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { AvatarProps } from '@mui/material/Avatar';

// third party
import PopupState, { bindPopper, bindToggle } from 'material-ui-popup-state';

// project imports
import Transitions from 'ui-component/extended/Transitions';
import MainCard from 'ui-component/cards/MainCard';
import DateLabel from 'ui-component/DateLabel';
import PriceLabel from 'ui-component/PriceLabel';
import { GlobalSearchResult, useGlobalSearch } from 'api/globalSearch';
import useDebounce from 'hooks/useDebounce';
import useTenantSelection from 'hooks/useTenantSelection';
import { visitDetailRoute } from 'utils/visitAccess';

// assets
import { IconAdjustmentsHorizontal, IconSearch, IconX } from '@tabler/icons-react';

// The user must type at least this many characters before a search fires — kept in step with
// api/globalSearch's own MIN_TERM_LENGTH so the "keep typing" message and the actual fetch agree.
const MIN_TERM_LENGTH = 2;

interface HeaderAvatarProps extends AvatarProps {
  children?: ReactNode;
}

const HeaderAvatar = forwardRef<HTMLDivElement, HeaderAvatarProps>(function HeaderAvatar({ children, ...others }, ref) {
  const theme = useTheme();

  return (
    <Avatar
      ref={ref}
      variant="circular"
      sx={{
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        // Path B Figma (2026-05-09): the settings/filter button inside the search
        // pill is a circle, not a rounded square. Override the 8px radius from
        // commonAvatar so this comes out fully round.
        borderRadius: '50%',
        color: theme.vars.palette.secondary.dark,
        background: theme.vars.palette.secondary.light,
        '&:hover': {
          color: theme.vars.palette.secondary.light,
          background: theme.vars.palette.secondary.dark
        },

        ...theme.applyStyles('dark', {
          color: theme.vars.palette.secondary.main,
          background: theme.palette.dark.main,
          '&:hover': {
            color: theme.vars.palette.secondary.light,
            background: theme.vars.palette.secondary.main
          }
        })
      }}
      {...others}
    >
      {children}
    </Avatar>
  );
});

// ==============================|| SEARCH RESULTS ||============================== //

interface SearchResultsProps {
  term: string;
  results: GlobalSearchResult[];
  includesFinancials: boolean;
  searching: boolean;
  searchError: unknown;
  onSelect: (result: GlobalSearchResult) => void;
}

/** The dropdown body shared by the desktop pill and the mobile popper — a docket per row. */
function SearchResults({ term, results, includesFinancials, searching, searchError, onSelect }: SearchResultsProps) {
  if (term.trim().length < MIN_TERM_LENGTH) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Keep typing to search dockets, vehicles and customers…
        </Typography>
      </Box>
    );
  }

  if (searchError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          Search failed. Try again.
        </Typography>
      </Box>
    );
  }

  if (searching && results.length === 0) {
    return (
      <Stack sx={{ p: 2 }} direction="row" spacing={1.5} alignItems="center">
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Searching…
        </Typography>
      </Stack>
    );
  }

  if (results.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No matching dockets, vehicles or customers.
        </Typography>
      </Box>
    );
  }

  return (
    <List
      dense
      sx={{
        width: '100%',
        maxHeight: 360,
        overflowY: 'auto',
        py: 0.5,
        '&::-webkit-scrollbar': { width: 5 }
      }}
    >
      {results.map((result) => (
        <ListItemButton key={`${result.visitId}`} onClick={() => onSelect(result)} sx={{ py: 1 }}>
          <ListItemText
            // The docket line carries a chip beside it, so ListItemText's own Typography wrapper
            // would nest a block inside a span. Both lines already bring their own Typography.
            disableTypography
            primary={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                  {result.docketNo ?? 'No docket #'}
                  {result.vehicleCode ? ` · ${result.vehicleCode}` : ''}
                </Typography>
                {/* A voided docket still matches the term, so it is shown — marked as withdrawn. */}
                {result.isVoided && (
                  <Chip
                    size="small"
                    color="error"
                    label="Voided"
                    sx={{ flexShrink: 0, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                  />
                )}
              </Stack>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" noWrap component="span">
                {result.customerName ?? 'No customer'}
              </Typography>
            }
          />
          <Stack sx={{ alignItems: 'flex-end', ml: 2, flexShrink: 0 }}>
            <DateLabel value={result.inVisitDate} variant="caption" color="text.secondary" />
            {includesFinancials && <PriceLabel value={result.netAmount} variant="body2" sx={{ fontWeight: 600 }} />}
          </Stack>
        </ListItemButton>
      ))}
    </List>
  );
}

// ==============================|| SEARCH INPUT - MOBILE||============================== //

interface MobileSearchProps {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  popupState: any;
  resultsProps: SearchResultsProps;
}

function MobileSearch({ value, setValue, popupState, resultsProps }: MobileSearchProps) {
  const theme = useTheme();

  return (
    <>
      <OutlinedInput
        id="input-search-header"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search"
        autoFocus
        startAdornment={
          <InputAdornment position="start">
            <IconSearch stroke={1.5} size="16px" />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            <HeaderAvatar>
              <IconAdjustmentsHorizontal stroke={1.5} size="20px" />
            </HeaderAvatar>
            <Box sx={{ ml: 2 }}>
              <Avatar
                variant="rounded"
                sx={{
                  ...theme.typography.commonAvatar,
                  ...theme.typography.mediumAvatar,
                  bgcolor: 'orange.light',
                  color: 'orange.dark',
                  '&:hover': { bgcolor: 'orange.dark', color: 'orange.light' },

                  ...theme.applyStyles('dark', { bgcolor: theme.palette.dark.main })
                }}
                {...bindToggle(popupState)}
              >
                <IconX stroke={1.5} size="20px" />
              </Avatar>
            </Box>
          </InputAdornment>
        }
        aria-describedby="search-helper-text"
        slotProps={{ input: { 'aria-label': 'weight', sx: { bgcolor: 'transparent', pl: 0.5 } } }}
        // Pill-shaped search per Path B Figma (2026-05-09). Tighter padding
        // and a lighter keyline to match Figma proportions.
        sx={{
          width: '100%',
          ml: 0.5,
          px: 1.5,
          height: 44,
          bgcolor: 'background.paper',
          borderRadius: 999,
          '& fieldset': { borderRadius: 999, borderColor: 'grey.200' }
        }}
      />
      <Card sx={{ mt: 1, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
        <SearchResults {...resultsProps} />
      </Card>
    </>
  );
}

// ==============================|| SEARCH INPUT ||============================== //

export default function SearchSection() {
  const navigate = useNavigate();
  const { selectedCompanySiteResult } = useTenantSelection();
  const siteId = selectedCompanySiteResult?.siteId ?? null;
  const companyId = selectedCompanySiteResult?.companyId ?? null;

  const [value, setValue] = useState('');
  const debouncedValue = useDebounce(value, 300);
  // Closed by ClickAwayListener/Escape without discarding what was typed, and reopened as soon as
  // the user changes the term — so dismissing a result list isn't mistaken for having no results.
  const [dismissed, setDismissed] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDismissed(false);
  }, [value]);

  const { results, includesFinancials, searching, searchError } = useGlobalSearch(debouncedValue, siteId, companyId);

  const showResults = value.trim().length >= MIN_TERM_LENGTH && !dismissed;

  const handleSelect = (result: GlobalSearchResult) => {
    setValue('');
    setDismissed(true);
    navigate(visitDetailRoute(result.visitId, result.siteId));
  };

  const resultsProps: SearchResultsProps = {
    term: debouncedValue,
    results,
    includesFinancials,
    searching,
    searchError,
    onSelect: handleSelect
  };

  return (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <PopupState variant="popper" popupId="global-search-popup-popper">
          {(popupState) => (
            <>
              <Box sx={{ ml: 2 }}>
                <HeaderAvatar {...bindToggle(popupState)}>
                  <IconSearch stroke={1.5} size="19.2px" />
                </HeaderAvatar>
              </Box>
              <Popper
                {...bindPopper(popupState)}
                transition
                sx={{ zIndex: 1100, width: '99%', top: '-55px !important', px: { xs: 1.25, sm: 1.5 } }}
              >
                {({ TransitionProps }) => (
                  <>
                    <Transitions type="zoom" {...TransitionProps} sx={{ transformOrigin: 'center left' }}>
                      <Card sx={{ bgcolor: 'background.default', border: 0, boxShadow: 'none' }}>
                        <Box sx={{ p: 2 }}>
                          <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                            <Grid size="grow">
                              <MobileSearch value={value} setValue={setValue} popupState={popupState} resultsProps={resultsProps} />
                            </Grid>
                          </Grid>
                        </Box>
                      </Card>
                    </Transitions>
                  </>
                )}
              </Popper>
            </>
          )}
        </PopupState>
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative' }} ref={anchorRef}>
        <OutlinedInput
          id="input-search-header"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setDismissed(true);
          }}
          placeholder="Search transactions, vehicles..."
          startAdornment={
            <InputAdornment position="start">
              <IconSearch stroke={1.5} size="16px" />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              <HeaderAvatar>
                <IconAdjustmentsHorizontal stroke={1.5} size="20px" />
              </HeaderAvatar>
            </InputAdornment>
          }
          aria-describedby="search-helper-text"
          slotProps={{ input: { 'aria-label': 'weight', sx: { bgcolor: 'transparent', pl: 0.5 } } }}
          // Pill-shaped search per Path B Figma (2026-05-09). Tighter padding
          // and a lighter keyline to match Figma proportions.
          sx={{
            width: { md: 250, lg: 434 },
            // Wider gap than the rest of the toolbar so the pill reads as separate from the tenant,
            // company and site pickers to its left rather than as a fourth field in that row.
            ml: 10,
            px: 1.5,
            height: 44,
            borderRadius: 999,
            '& fieldset': { borderRadius: 999, borderColor: 'grey.200' },
            '&:hover fieldset': { borderColor: 'grey.300 !important' }
          }}
        />
        <Popper
          open={showResults}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          transition
          sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={() => setDismissed(true)}>
              <Transitions in={showResults} {...TransitionProps}>
                <MainCard border elevation={16} content={false} boxShadow>
                  <SearchResults {...resultsProps} />
                </MainCard>
              </Transitions>
            </ClickAwayListener>
          )}
        </Popper>
      </Box>
    </>
  );
}

HeaderAvatar.propTypes = { children: PropTypes.node };

MobileSearch.propTypes = { value: PropTypes.string, setValue: PropTypes.func, popupState: PropTypes.any };
